import { Injectable } from '@angular/core'
import {
  AccountInfo,
  CreateAccountParams,
  CreateAccountProfileParams,
  CreateAccountResult,
  CreateAccountUserParams,
  ProfileInfo,
  UserDuplicateLookupParams
} from '@se-po/shared-data-access-models'
import { DobParser, PhoneParser } from '@se-po/shared-data-access-parsers'
import { UserServiceResources as us } from 'se-resource-types'

import { StoreService } from './store.service'

@Injectable({
  providedIn: 'root'
})
export class HouseholdStoreService implements StoreService {
  public address: us.Address
  public authSession: us.AuthSession
  public basePath = '/household'
  public captchaResponse: string
  public childAddressSame: boolean
  public createdAccount: CreateAccountResult
  public invite: us.Invitation
  public nextPath: string
  public queryStringEmail?: string
  private _dupes: us.UserDuplicateLookupResponse
  private _dupesChecked = false
  private _accountInfo: AccountInfo
  private _childInfo: ProfileInfo

  public get accountInfo(): AccountInfo {
    return this._accountInfo
  }

  public get disableEmail(): boolean {
    // we will prefill email from admin_invites, but allow them to be changed
    return !!this.authSessionEmailAddress && this.invite?.originator_type !== 'admin_invite'
  }

  public get dupes(): us.UserDuplicateLookupResponse {
    return this._dupes
  }

  public get dupesChecked(): boolean {
    return this._dupesChecked
  }

  public get createAccountParams(): CreateAccountParams {
    const user: CreateAccountUserParams = {
      allow_special_offers: this.accountInfo?.specialOffers ? '1' : '0',
      allow_third_party_mailing: this.accountInfo?.thirdPartyMailing ? '1' : '0',
      date_of_birth: DobParser.mmToIso(this.dateOfBirth),
      first_name: this.accountInfo?.firstName,
      gender: this.accountInfo?.gender,
      last_name: this.accountInfo?.lastName,
      nickname: this.accountInfo?.preferredName,
      password: this.accountInfo?.password,
      phone: PhoneParser.toN(this.accountInfo?.phone),
      suffix: this.accountInfo?.suffix
    }
    if (!this.disableEmail) {
      user.email_on_create = this.accountInfo?.email
    }
    const params: CreateAccountParams = {
      user,
      'g-recaptcha-response': this.captchaResponse
    }
    if (this.address) {
      params.addresses = [this.address]
    }
    if (this.createAccountProfileParams) {
      params.profiles = [this.createAccountProfileParams]
    }

    return params
  }

  public get createAccountProfileParams(): CreateAccountProfileParams {
    if (!this.childInfo) return undefined

    return {
      addresses: this.childInfo.address ? [this.childInfo.address] : [],
      date_of_birth: this.childInfo.dateOfBirth,
      email_addresses: this.childInfo.email ? [{ address: this.childInfo.email }] : [],
      first_name: this.childInfo.firstName,
      gender: this.childInfo.gender,
      graduation_year: this.childInfo.gradYear,
      last_name: this.childInfo.lastName,
      nickname: this.childInfo.preferredName,
      // eslint-disable-next-line id-blacklist
      phone_numbers: this.childInfo.phone ? [{ number: this.childInfo.phone, phone_type: 'cellPhone' }] : [],
      suffix: this.childInfo.suffix
    }
  }

  public get dateOfBirth(): string {
    return this.accountInfo?.dateOfBirth
  }

  public get userDuplicateLookupParams(): UserDuplicateLookupParams {
    return {
      first_name: this.accountInfo.firstName,
      last_name: this.accountInfo.lastName,
      email: this.emailAddress,
      date_of_birth: DobParser.mmToIso(this.accountInfo.dateOfBirth),
      phone: PhoneParser.toN(this.accountInfo.phone),
      originator: 'account_create',
    }
  }

  public get emailAddress(): string {
    return this.accountInfo?.email || this.authSessionEmailAddress
  }

  public get authSessionEmailAddress(): string {
    const email: any = this.authSession?.email_address || this.authSession?.email_on_create || this.queryStringEmail

    // There was a scenario where auth_session.email_address
    // could be either a string or an object.
    // This has been updated to always be a string now.
    // Leaving this as-is for now since its not hurting anything, and you know, just in case
    if (typeof email === 'object') return email?.address
    if (typeof email === 'string') return email
    return undefined
  }

  public get accountValid(): boolean {
    if (!this.accountInfo) return false
    if (!this.accountInfo.firstName) return false
    if (!this.emailAddress) return false
    if (!this.dupesChecked) return false

    return true
  }

  public get createdAccountValid(): boolean {
    if (!this.createdAccount) return false
    if (!this.createdAccount.uuid) return false

    return true
  }

  public get childInfo(): ProfileInfo {
    return this._childInfo
  }

  public get childPersona(): us.Persona {
    if (!this.childInfo) return undefined

    return {
      first_name: this.childInfo.firstName,
      last_name: this.childInfo.lastName,
      date_of_birth: this.childInfo.dateOfBirth,
      gender: this.childInfo.gender,
      graduation_year: this.childInfo.gradYear,
      nickname: this.childInfo.preferredName,
      suffix: this.childInfo.suffix
    } as us.Persona
  }

  public set dupes(d: us.UserDuplicateLookupResponse) {
    this._dupes = d
    this._dupesChecked = true
  }

  public set accountInfo(data: any) {
    if (!data) {
      this._accountInfo = undefined
      return
    }

    this._accountInfo = {
      dateOfBirth: data.accountInfo?.dateOfBirth,
      email: data.contactInfo?.email,
      firstName: data.accountInfo?.name?.first,
      gender: data.accountInfo?.gender,
      lastName: data.accountInfo?.name?.last,
      password: data.password,
      phone: data.contactInfo?.phone,
      preferredName: data.accountInfo?.name?.preferred,
      specialOffers: data.specialOffers,
      suffix: data.accountInfo?.name?.suffix,
      thirdPartyMailing: data.thirdPartyMailing,
    }
  }

  public set childInfo(data: any) {
    if (!data) {
      this._childInfo = undefined
      return
    }

    this._childInfo = {
      address: data.addresses ? data.addresses[0] : undefined,
      firstName: data.name?.first,
      lastName: data.name?.last,
      preferredName: data.name?.preferred,
      suffix: data.name?.suffix,
      gender: data.gender,
      dateOfBirth: data.dateOfBirth,
      gradYear: data.gradYear,
      phone: data.phone,
      email: data.email,
    }
  }

  public patchAccountInfo(data: any): void {
    this._accountInfo = { ...(this._accountInfo || {}), ...data }
  }

  public patchChildInfo(data: any): void {
    this._childInfo = { ...(this._childInfo || {}), ...data }
  }

  public reset(): void {
    this._accountInfo = undefined
    this._dupes = undefined
    this._dupesChecked = false
  }
}
